import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { serviceAPI, staffAPI, productAPI, appointmentAPI, orderAPI, categoryAPI, storeAPI, imageAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { useBrand } from '../context/BrandContext';
import { formatPrice, formatShortDate, getStatusLabel, getStatusColor } from '../lib/utils';
import { toast } from 'sonner';
import ImportExportSection from '../components/ImportExportSection';
import StaffPresenceSection from '../components/StaffPresenceSection';
import DiscountCodesSection from '../components/DiscountCodesSection';
import WaitlistSection from '../components/WaitlistSection';
import ImageUploader from '../components/ImageUploader';
import CustomDomainSection from '../components/CustomDomainSection';
import WhatsAppSection from '../components/WhatsAppSection';
import AdvancedAnalytics from '../components/AdvancedAnalytics';
import LoyaltySection from '../components/LoyaltySection';
import StaffScheduleManager from '../components/StaffScheduleManager';
import AppointmentManager from '../components/AppointmentManager';
import StaffManager from '../components/StaffManager';
import ServiceManager from '../components/ServiceManager';
import ProductManager from '../components/ProductManager';
import CategoryManager from '../components/CategoryManager';
import ReminderSettings from '../components/ReminderSettings';
import {
  Scissors,
  Calendar,
  ShoppingBag,
  Users,
  Settings,
  LogOut,
  Plus,
  Package,
  BarChart3,
  Menu,
  X,
  Trash2,
  Edit,
  Clock,
  CalendarX,
  Tag,
  Store as StoreIcon,
  Palette,
  Image,
  Instagram,
  Facebook,
  Globe,
  MessageCircle,
  FileSpreadsheet,
  UserCheck,
  Percent,
  ListOrdered,
  Gift,
  CalendarClock,
  Bell
} from 'lucide-react';


export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { store, refreshStore, modules, hasShop, hasReportsAdv, hasStaffPresence, hasWhatsApp } = useStore();
  const { refreshBranding, brand, isSingleStore } = useBrand();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('reports');
  
  // Data states
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [products, setProducts] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [storeHours, setStoreHours] = useState([]);
  const [closures, setClosures] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [serviceDialog, setServiceDialog] = useState(false);
  const [productDialog, setProductDialog] = useState(false);
  const [categoryDialog, setCategoryDialog] = useState(false);
  const [closureDialog, setClosureDialog] = useState(false);
  const [staffServicesDialog, setStaffServicesDialog] = useState({ open: false, staff: null });
  const [staffServicesConfig, setStaffServicesConfig] = useState({ 
    can_do_all_services: true, 
    allowed_services: [], 
    allowed_categories: [] 
  });
  const [savingStaffServices, setSavingStaffServices] = useState(false);
  
  // Form states
  const [newService, setNewService] = useState({ name: '', description: '', duration_minutes: 30, price: 0, category_id: '', image_url: '' });
  const [newProduct, setNewProduct] = useState({ name: '', description: '', price: 0, stock: 0, image_url: '', category_id: '' });
  const [newCategory, setNewCategory] = useState({ name: '', description: '', type: 'service', image_url: '' });
  const [newClosure, setNewClosure] = useState({ date: '', reason: '' });
  
  // Store settings - extended with branding
  const [storeSettings, setStoreSettings] = useState({
    name: '',
    description: '',
    address: '',
    phone: '',
    email: '',
    // Branding
    logo_url: '',
    tagline: '',
    primary_color: '#047857',
    secondary_color: '#f3f4f6',
    footer_text: '',
    // Social links
    instagram: '',
    facebook: '',
    whatsapp: '',
    website: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (store) {
      setStoreSettings({
        name: store.name || '',
        description: store.description || '',
        address: store.address || '',
        phone: store.phone || '',
        email: store.email || '',
        // Branding
        logo_url: store.branding?.logo_url || '',
        tagline: store.branding?.tagline || '',
        primary_color: store.branding?.primary_color || '#047857',
        secondary_color: store.branding?.secondary_color || '#f3f4f6',
        footer_text: store.branding?.footer_text || '',
        // Social links
        instagram: store.branding?.social_links?.instagram || '',
        facebook: store.branding?.social_links?.facebook || '',
        whatsapp: store.branding?.social_links?.whatsapp || '',
        website: store.branding?.social_links?.website || ''
      });
    }
  }, [store]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [servicesRes, staffRes, productsRes, appointmentsRes, ordersRes, categoriesRes, hoursRes, closuresRes] = await Promise.all([
        serviceAPI.list().catch(() => ({ data: [] })),
        staffAPI.list().catch(() => ({ data: [] })),
        productAPI.list().catch(() => ({ data: [] })),
        appointmentAPI.list({}).catch(() => ({ data: [] })),
        orderAPI.list().catch(() => ({ data: [] })),
        categoryAPI.list().catch(() => ({ data: [] })),
        storeAPI.getHours().catch(() => ({ data: [] })),
        storeAPI.getClosures().catch(() => ({ data: [] }))
      ]);
      
      setServices(servicesRes.data);
      setStaff(staffRes.data);
      setProducts(productsRes.data);
      setAppointments(appointmentsRes.data);
      setOrders(ordersRes.data);
      setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);
      setStoreHours(Array.isArray(hoursRes.data) ? hoursRes.data : []);
      setClosures(Array.isArray(closuresRes.data) ? closuresRes.data : []);
    } catch (error) {
      console.error('Errore caricamento dati:', error);
    } finally {
      setLoading(false);
    }
  };

  // Individual refresh functions for components
  const loadStaff = async () => {
    try {
      const res = await staffAPI.list();
      setStaff(res.data || []);
    } catch (error) {
      console.error('Errore caricamento staff:', error);
    }
  };

  const loadAppointments = async () => {
    try {
      const res = await appointmentAPI.list({});
      setAppointments(res.data || []);
    } catch (error) {
      console.error('Errore caricamento appuntamenti:', error);
    }
  };

  const loadServices = async () => {
    try {
      const res = await serviceAPI.list();
      setServices(res.data || []);
    } catch (error) {
      console.error('Errore caricamento servizi:', error);
    }
  };

  const loadProducts = async () => {
    try {
      const res = await productAPI.list();
      setProducts(res.data || []);
    } catch (error) {
      console.error('Errore caricamento prodotti:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const res = await categoryAPI.list();
      setCategories(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Errore caricamento categorie:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logout effettuato');
  };

  // Service handlers
  const handleCreateService = async () => {
    try {
      await serviceAPI.create(newService);
      toast.success('Servizio creato');
      setServiceDialog(false);
      setNewService({ name: '', description: '', duration_minutes: 30, price: 0, category_id: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore creazione servizio');
    }
  };

  const handleDeleteService = async (id) => {
    if (!window.confirm('Eliminare questo servizio?')) return;
    try {
      await serviceAPI.delete(id);
      toast.success('Servizio eliminato');
      loadData();
    } catch (error) {
      toast.error('Errore eliminazione servizio');
    }
  };

  // Product handlers
  const handleCreateProduct = async () => {
    try {
      await productAPI.create(newProduct);
      toast.success('Prodotto creato');
      setProductDialog(false);
      setNewProduct({ name: '', description: '', price: 0, stock: 0, image_url: '', category_id: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore creazione prodotto');
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Eliminare questo prodotto?')) return;
    try {
      await productAPI.delete(id);
      toast.success('Prodotto eliminato');
      loadData();
    } catch (error) {
      toast.error('Errore eliminazione prodotto');
    }
  };

  // Category handlers
  const handleCreateCategory = async () => {
    try {
      await categoryAPI.create(newCategory);
      toast.success('Categoria creata');
      setCategoryDialog(false);
      setNewCategory({ name: '', description: '', type: 'service' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore creazione categoria');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Eliminare questa categoria?')) return;
    try {
      await categoryAPI.delete(id);
      toast.success('Categoria eliminata');
      loadData();
    } catch (error) {
      toast.error('Errore eliminazione categoria');
    }
  };

  // Closure handlers
  const handleCreateClosure = async () => {
    try {
      await storeAPI.createClosure(newClosure.date, newClosure.reason);
      toast.success('Chiusura aggiunta');
      setClosureDialog(false);
      setNewClosure({ date: '', reason: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore aggiunta chiusura');
    }
  };

  const handleDeleteClosure = async (id) => {
    if (!window.confirm('Rimuovere questa chiusura?')) return;
    try {
      await storeAPI.deleteClosure(id);
      toast.success('Chiusura rimossa');
      loadData();
    } catch (error) {
      toast.error('Errore rimozione chiusura');
    }
  };

  // Order status handler
  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await orderAPI.updateStatus(orderId, status);
      toast.success('Stato ordine aggiornato');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore aggiornamento stato');
    }
  };

  // Appointment status handler
  const handleUpdateAppointmentStatus = async (appointmentId, status) => {
    try {
      await appointmentAPI.updateStatus(appointmentId, status);
      toast.success('Stato appuntamento aggiornato');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore aggiornamento stato');
    }
  };

  // Staff services handlers
  const openStaffServicesDialog = async (member) => {
    try {
      const response = await staffAPI.getServices(member.id);
      setStaffServicesConfig({
        can_do_all_services: response.can_do_all_services ?? true,
        allowed_services: response.allowed_services || [],
        allowed_categories: response.allowed_categories || []
      });
      setStaffServicesDialog({ open: true, staff: member });
    } catch (error) {
      // Se l'endpoint non esiste ancora, usa i default
      setStaffServicesConfig({
        can_do_all_services: member.can_do_all_services ?? true,
        allowed_services: member.allowed_services || [],
        allowed_categories: member.allowed_categories || []
      });
      setStaffServicesDialog({ open: true, staff: member });
    }
  };

  const handleSaveStaffServices = async () => {
    if (!staffServicesDialog.staff) return;
    setSavingStaffServices(true);
    try {
      await staffAPI.updateServices(staffServicesDialog.staff.id, staffServicesConfig);
      toast.success('Servizi staff aggiornati');
      setStaffServicesDialog({ open: false, staff: null });
      loadData();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSavingStaffServices(false);
    }
  };

  const toggleServiceForStaff = (serviceId) => {
    setStaffServicesConfig(prev => ({
      ...prev,
      allowed_services: prev.allowed_services.includes(serviceId)
        ? prev.allowed_services.filter(id => id !== serviceId)
        : [...prev.allowed_services, serviceId]
    }));
  };

  const toggleCategoryForStaff = (categoryId) => {
    setStaffServicesConfig(prev => ({
      ...prev,
      allowed_categories: prev.allowed_categories.includes(categoryId)
        ? prev.allowed_categories.filter(id => id !== categoryId)
        : [...prev.allowed_categories, categoryId]
    }));
  };

  // Store settings handler
  const handleSaveStoreSettings = async () => {
    try {
      await storeAPI.update(store.id, {
        name: storeSettings.name,
        description: storeSettings.description,
        address: storeSettings.address,
        phone: storeSettings.phone,
        email: storeSettings.email,
        branding: {
          logo_url: storeSettings.logo_url,
          tagline: storeSettings.tagline,
          primary_color: storeSettings.primary_color,
          secondary_color: storeSettings.secondary_color,
          footer_text: storeSettings.footer_text,
          social_links: {
            instagram: storeSettings.instagram,
            facebook: storeSettings.facebook,
            whatsapp: storeSettings.whatsapp,
            website: storeSettings.website
          }
        }
      });
      toast.success('Impostazioni salvate');
      if (refreshStore) refreshStore();
      if (refreshBranding) refreshBranding();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore salvataggio impostazioni');
    }
  };

  // Stats
  const todayAppointments = appointments.filter(
    apt => apt.date === new Date().toISOString().split('T')[0]
  ).length;
  const totalRevenue = orders
    .filter(o => o.payment_status === 'PAID')
    .reduce((sum, o) => sum + (o.total || 0), 0);

  const orderStatuses = [
    { value: 'pending', label: 'In attesa' },
    { value: 'confirmed', label: 'Confermato' },
    { value: 'processing', label: 'In lavorazione' },
    { value: 'shipped', label: 'Spedito' },
    { value: 'delivered', label: 'Consegnato' },
    { value: 'cancelled', label: 'Annullato' }
  ];

  const appointmentStatuses = [
    { value: 'IN_ATTESA', label: 'In attesa' },
    { value: 'CONFERMATO', label: 'Confermato' },
    { value: 'COMPLETATO', label: 'Completato' },
    { value: 'ANNULLATO', label: 'Annullato' }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar toggle */}
      <button
        className="fixed top-4 left-4 z-50 md:hidden bg-background p-2 rounded-lg border"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        data-testid="mobile-menu-toggle"
      >
        {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? '' : 'collapsed'} md:translate-x-0`}>
        <div className="p-4 border-b">
          <div className="flex items-center gap-2">
            <Scissors className="w-6 h-6 text-primary" />
            <span className="font-semibold text-sm">Admin Negozio</span>
          </div>
        </div>
        
        <nav className="py-2 overflow-y-auto" style={{maxHeight: 'calc(100vh - 140px)'}}>
          <div className="nav-section-title">Gestione</div>
          
          {/* Report - sempre visibile, ma mostra analytics avanzate solo se REPORTS_ADV */}
          <div 
            className={`nav-item cursor-pointer ${activeTab === 'reports' ? 'active' : ''}`}
            onClick={() => setActiveTab('reports')}
            data-testid="nav-reports"
          >
            <BarChart3 className="w-4 h-4" />
            <span>Report</span>
            {hasReportsAdv && <Badge className="ml-auto bg-emerald-600 text-[10px] px-1">ADV</Badge>}
          </div>
          
          {/* Appuntamenti - BASE, sempre visibile */}
          <div 
            className={`nav-item cursor-pointer ${activeTab === 'appointments' ? 'active' : ''}`}
            onClick={() => setActiveTab('appointments')}
            data-testid="nav-appointments"
          >
            <Calendar className="w-4 h-4" />
            <span>Appuntamenti</span>
          </div>
          
          {/* Ordini - solo se SHOP attivo */}
          {hasShop && (
            <div 
              className={`nav-item cursor-pointer ${activeTab === 'orders' ? 'active' : ''}`}
              onClick={() => setActiveTab('orders')}
              data-testid="nav-orders"
            >
              <ShoppingBag className="w-4 h-4" />
              <span>Ordini</span>
            </div>
          )}
          
          {/* Lista Attesa - BASE, sempre visibile */}
          <div 
            className={`nav-item cursor-pointer ${activeTab === 'waitlist' ? 'active' : ''}`}
            onClick={() => setActiveTab('waitlist')}
            data-testid="nav-waitlist"
          >
            <ListOrdered className="w-4 h-4" />
            <span>Lista Attesa</span>
          </div>
          
          <div className="nav-divider"></div>
          <div className="nav-section-title">Catalogo</div>
          
          {/* Servizi - BASE, sempre visibile */}
          <div 
            className={`nav-item cursor-pointer ${activeTab === 'services' ? 'active' : ''}`}
            onClick={() => setActiveTab('services')}
            data-testid="nav-services"
          >
            <Scissors className="w-4 h-4" />
            <span>Servizi</span>
          </div>
          
          {/* Prodotti - solo se SHOP attivo */}
          {hasShop && (
            <div 
              className={`nav-item cursor-pointer ${activeTab === 'products' ? 'active' : ''}`}
              onClick={() => setActiveTab('products')}
              data-testid="nav-products"
            >
              <Package className="w-4 h-4" />
              <span>Prodotti</span>
            </div>
          )}
          
          {/* Categorie - BASE, sempre visibile */}
          <div 
            className={`nav-item cursor-pointer ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => setActiveTab('categories')}
            data-testid="nav-categories"
          >
            <Tag className="w-4 h-4" />
            <span>Categorie</span>
          </div>
          
          {/* Staff - BASE, sempre visibile */}
          <div 
            className={`nav-item cursor-pointer ${activeTab === 'staff' ? 'active' : ''}`}
            onClick={() => setActiveTab('staff')}
            data-testid="nav-staff"
          >
            <Users className="w-4 h-4" />
            <span>Staff</span>
          </div>
          
          {/* Turni Staff */}
          <div 
            className={`nav-item cursor-pointer ${activeTab === 'schedule' ? 'active' : ''}`}
            onClick={() => setActiveTab('schedule')}
            data-testid="nav-schedule"
          >
            <CalendarClock className="w-4 h-4" />
            <span>Turni</span>
          </div>
          
          <div className="nav-divider"></div>
          <div className="nav-section-title">Strumenti</div>
          
          {/* Presenze Staff - solo se STAFF_PRESENCE attivo */}
          {hasStaffPresence && (
            <div 
              className={`nav-item cursor-pointer ${activeTab === 'presence' ? 'active' : ''}`}
              onClick={() => setActiveTab('presence')}
              data-testid="nav-presence"
            >
              <UserCheck className="w-4 h-4" />
              <span>Presenze</span>
            </div>
          )}
          
          {/* Sconti - solo se SHOP attivo */}
          {hasShop && (
            <div 
              className={`nav-item cursor-pointer ${activeTab === 'discounts' ? 'active' : ''}`}
              onClick={() => setActiveTab('discounts')}
              data-testid="nav-discounts"
            >
              <Percent className="w-4 h-4" />
              <span>Sconti</span>
            </div>
          )}
          
          {/* WhatsApp - solo se WHATSAPP attivo */}
          {hasWhatsApp && (
            <div 
              className={`nav-item cursor-pointer ${activeTab === 'whatsapp' ? 'active' : ''}`}
              onClick={() => setActiveTab('whatsapp')}
              data-testid="nav-whatsapp"
            >
              <MessageCircle className="w-4 h-4" />
              <span>WhatsApp</span>
            </div>
          )}
          
          {/* Import/Export - BASE, sempre visibile */}
          <div 
            className={`nav-item cursor-pointer ${activeTab === 'import-export' ? 'active' : ''}`}
            onClick={() => setActiveTab('import-export')}
            data-testid="nav-import-export"
          >
            <FileSpreadsheet className="w-4 h-4" />
            <span>Import/Export</span>
          </div>
          
          {/* Fedeltà */}
          <div 
            className={`nav-item cursor-pointer ${activeTab === 'loyalty' ? 'active' : ''}`}
            onClick={() => setActiveTab('loyalty')}
            data-testid="nav-loyalty"
          >
            <Gift className="w-4 h-4" />
            <span>Fedeltà</span>
          </div>
          
          {/* Promemoria */}
          <div 
            className={`nav-item cursor-pointer ${activeTab === 'reminders' ? 'active' : ''}`}
            onClick={() => setActiveTab('reminders')}
            data-testid="nav-reminders"
          >
            <Bell className="w-4 h-4" />
            <span>Promemoria</span>
          </div>
          
          <div className="nav-divider"></div>
          
          {/* Impostazioni - BASE, sempre visibile */}
          <div 
            className={`nav-item cursor-pointer ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
            data-testid="nav-settings"
          >
            <Settings className="w-4 h-4" />
            <span>Impostazioni</span>
          </div>
        </nav>
        
        <div className="absolute bottom-4 left-0 right-0 px-2">
          <button 
            onClick={handleLogout}
            className="nav-item w-full text-destructive hover:text-destructive"
            data-testid="admin-logout"
          >
            <LogOut className="w-4 h-4" />
            <span>Esci</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="md:ml-72 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Dashboard Admin</h1>
            <p className="text-muted-foreground">
              Benvenuto, {user?.name} - {store?.name || 'Negozio'}
            </p>
          </div>

          {/* Stats */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Appuntamenti Oggi</p>
                    <p className="text-2xl font-bold">{todayAppointments}</p>
                  </div>
                  <Calendar className="w-8 h-8 text-primary" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Servizi</p>
                    <p className="text-2xl font-bold">{services.length}</p>
                  </div>
                  <Scissors className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            
            {hasShop && (
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Prodotti</p>
                      <p className="text-2xl font-bold">{products.length}</p>
                    </div>
                    <Package className="w-8 h-8 text-green-500" />
                  </div>
                </CardContent>
              </Card>
            )}
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Incassi</p>
                    <p className="text-2xl font-bold">{formatPrice(totalRevenue)}</p>
                  </div>
                  <BarChart3 className="w-8 h-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Reports Tab */}
          {activeTab === 'reports' && <AdvancedAnalytics />}
          
          {/* Services Tab */}
          {activeTab === 'services' && (
            <ServiceManager 
              services={services}
              categories={categories}
              onRefresh={loadServices}
            />
          )}
          
          {/* Staff Tab */}
          {activeTab === 'staff' && (
            <StaffManager 
              staff={staff}
              categories={categories.filter(c => c.type === 'service')}
              services={services}
              onRefresh={loadStaff}
            />
          )}
          
          {/* Products Tab - solo se SHOP attivo */}
          {activeTab === 'products' && hasShop && (
            <ProductManager 
              products={products}
              categories={categories}
              onRefresh={loadProducts}
            />
          )}
          
          {/* Categories Tab */}
          {activeTab === 'categories' && (
            <CategoryManager 
              categories={categories}
              services={services}
              products={products}
              onRefresh={loadCategories}
            />
          )}
          
          {/* Appointments Tab */}
          {activeTab === 'appointments' && (
            <AppointmentManager 
              appointments={appointments} 
              onRefresh={loadAppointments}
            />
          )}
          
          {/* Orders Tab - solo se SHOP attivo */}
          {activeTab === 'orders' && hasShop && (
            <Card>
              <CardHeader>
                <CardTitle>Gestione Ordini</CardTitle>
              </CardHeader>
              <CardContent>
                {orders.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">Nessun ordine</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Prodotti</th>
                          <th>Totale</th>
                          <th>Pagamento</th>
                          <th>Stato</th>
                          <th>Azioni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.slice(0, 50).map(order => (
                          <tr key={order.id}>
                            <td>#{order.id.slice(0, 8)}</td>
                            <td>{order.items?.length || 0} articoli</td>
                            <td>{formatPrice(order.total)}</td>
                            <td>
                              <Badge className={`status-badge ${getStatusColor(order.payment_status)}`}>
                                {getStatusLabel(order.payment_status)}
                              </Badge>
                            </td>
                            <td>{order.status || 'pending'}</td>
                            <td>
                              <Select 
                                value={order.status || 'pending'} 
                                onValueChange={(v) => handleUpdateOrderStatus(order.id, v)}
                              >
                                <SelectTrigger className="w-36">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {orderStatuses.map(s => (
                                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Staff Presence Tab - solo se STAFF_PRESENCE attivo */}
          {activeTab === 'presence' && hasStaffPresence && (
            <StaffPresenceSection />
          )}

          {/* Staff Schedule Tab */}
          {activeTab === 'schedule' && (
            <StaffScheduleManager />
          )}

          {/* Discount Codes Tab - solo se SHOP attivo */}
          {activeTab === 'discounts' && hasShop && (
            <DiscountCodesSection />
          )}

          {/* Waitlist Tab */}
          {activeTab === 'waitlist' && (
            <WaitlistSection />
          )}

          {/* Import/Export Tab */}
          {activeTab === 'import-export' && (
            <ImportExportSection onDataChange={loadData} />
          )}

          {/* Loyalty Tab */}
          {activeTab === 'loyalty' && (
            <LoyaltySection />
          )}

          {/* Reminders Tab */}
          {activeTab === 'reminders' && (
            <ReminderSettings storeSlug={store?.slug} />
          )}

          {/* WhatsApp Tab - solo se WHATSAPP attivo */}
          {activeTab === 'whatsapp' && hasWhatsApp && (
            <WhatsAppSection />
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              {/* Store Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <StoreIcon className="w-5 h-5" />
                    Informazioni Negozio
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Nome Negozio</Label>
                      <Input 
                        value={storeSettings.name}
                        onChange={(e) => setStoreSettings({...storeSettings, name: e.target.value})}
                        data-testid="store-name-input"
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input 
                        type="email"
                        value={storeSettings.email}
                        onChange={(e) => setStoreSettings({...storeSettings, email: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Telefono</Label>
                      <Input 
                        value={storeSettings.phone}
                        onChange={(e) => setStoreSettings({...storeSettings, phone: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Indirizzo</Label>
                      <Input 
                        value={storeSettings.address}
                        onChange={(e) => setStoreSettings({...storeSettings, address: e.target.value})}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Descrizione</Label>
                    <Textarea 
                      value={storeSettings.description}
                      onChange={(e) => setStoreSettings({...storeSettings, description: e.target.value})}
                      rows={3}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Branding */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Palette className="w-5 h-5" />
                    Personalizzazione Brand
                  </CardTitle>
                  <CardDescription>
                    Personalizza l&apos;aspetto del tuo negozio
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Logo & Tagline */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <ImageUploader
                        currentImage={storeSettings.logo_url}
                        onImageChange={(url) => setStoreSettings({...storeSettings, logo_url: url})}
                        label="Logo Negozio"
                        aspectRatio="landscape"
                        maxSizeMB={1}
                      />
                    </div>
                    <div>
                      <Label>Tagline / Slogan</Label>
                      <Input 
                        value={storeSettings.tagline}
                        onChange={(e) => setStoreSettings({...storeSettings, tagline: e.target.value})}
                        placeholder="Il tuo barbiere di fiducia"
                        data-testid="tagline-input"
                      />
                    </div>
                  </div>
                  
                  {/* Colors */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Colore Primario</Label>
                      <div className="flex items-center gap-2">
                        <input 
                          type="color"
                          value={storeSettings.primary_color}
                          onChange={(e) => setStoreSettings({...storeSettings, primary_color: e.target.value})}
                          className="w-10 h-10 rounded border cursor-pointer"
                        />
                        <Input 
                          value={storeSettings.primary_color}
                          onChange={(e) => setStoreSettings({...storeSettings, primary_color: e.target.value})}
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Colore Secondario</Label>
                      <div className="flex items-center gap-2">
                        <input 
                          type="color"
                          value={storeSettings.secondary_color}
                          onChange={(e) => setStoreSettings({...storeSettings, secondary_color: e.target.value})}
                          className="w-10 h-10 rounded border cursor-pointer"
                        />
                        <Input 
                          value={storeSettings.secondary_color}
                          onChange={(e) => setStoreSettings({...storeSettings, secondary_color: e.target.value})}
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Footer Text */}
                  <div>
                    <Label>Testo Footer</Label>
                    <Input 
                      value={storeSettings.footer_text}
                      onChange={(e) => setStoreSettings({...storeSettings, footer_text: e.target.value})}
                      placeholder="© 2025 Nome Negozio. Tutti i diritti riservati."
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Social Links */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="w-5 h-5" />
                    Link Social
                  </CardTitle>
                  <CardDescription>
                    Aggiungi i link ai tuoi profili social
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="flex items-center gap-2">
                        <Instagram className="w-4 h-4" />
                        Instagram
                      </Label>
                      <Input 
                        value={storeSettings.instagram}
                        onChange={(e) => setStoreSettings({...storeSettings, instagram: e.target.value})}
                        placeholder="https://instagram.com/tuoprofilo"
                      />
                    </div>
                    <div>
                      <Label className="flex items-center gap-2">
                        <Facebook className="w-4 h-4" />
                        Facebook
                      </Label>
                      <Input 
                        value={storeSettings.facebook}
                        onChange={(e) => setStoreSettings({...storeSettings, facebook: e.target.value})}
                        placeholder="https://facebook.com/tuapagina"
                      />
                    </div>
                    <div>
                      <Label className="flex items-center gap-2">
                        <MessageCircle className="w-4 h-4" />
                        WhatsApp
                      </Label>
                      <Input 
                        value={storeSettings.whatsapp}
                        onChange={(e) => setStoreSettings({...storeSettings, whatsapp: e.target.value})}
                        placeholder="https://wa.me/39123456789"
                      />
                    </div>
                    <div>
                      <Label className="flex items-center gap-2">
                        <Globe className="w-4 h-4" />
                        Sito Web
                      </Label>
                      <Input 
                        value={storeSettings.website}
                        onChange={(e) => setStoreSettings({...storeSettings, website: e.target.value})}
                        placeholder="https://www.tuosito.it"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Custom Domain */}
              <CustomDomainSection />

              {/* Closures */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <CalendarX className="w-5 h-5" />
                      Giorni di Chiusura
                    </CardTitle>
                    <CardDescription>Gestisci ferie e chiusure straordinarie</CardDescription>
                  </div>
                  <Dialog open={closureDialog} onOpenChange={setClosureDialog}>
                    <DialogTrigger asChild>
                      <Button variant="outline" data-testid="add-closure-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Aggiungi Chiusura
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Nuova Chiusura</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label>Data</Label>
                          <Input 
                            type="date"
                            value={newClosure.date}
                            onChange={(e) => setNewClosure({...newClosure, date: e.target.value})}
                            data-testid="closure-date-input"
                          />
                        </div>
                        <div>
                          <Label>Motivo (opzionale)</Label>
                          <Input 
                            value={newClosure.reason}
                            onChange={(e) => setNewClosure({...newClosure, reason: e.target.value})}
                            placeholder="Es: Ferie, Festività..."
                          />
                        </div>
                        <Button onClick={handleCreateClosure} className="w-full" data-testid="save-closure-btn">
                          Aggiungi Chiusura
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent>
                  {closures.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">Nessuna chiusura programmata</p>
                  ) : (
                    <div className="space-y-2">
                      {closures.map(closure => (
                        <div key={closure.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                          <div>
                            <p className="font-medium">{formatShortDate(closure.date)}</p>
                            {closure.reason && <p className="text-sm text-muted-foreground">{closure.reason}</p>}
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteClosure(closure.id)}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Save Button */}
              <div className="flex justify-end">
                <Button onClick={handleSaveStoreSettings} size="lg" data-testid="save-settings-btn">
                  Salva Impostazioni
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
